import type { TFunction } from 'i18next';
import { specialMaterialRules, type SpecialMaterialInput } from '@kreart/shared';
import type { CatalogItemFlag } from '@/features/project-items/shared/pickers/CatalogAutosuggest';

/**
 * Turns the engine's special-pricing rules (`@kreart/shared/special-materials`)
 * into the advisory lines a material picker shows.
 *
 * Only the wording lives here — which rules a board triggers is decided in
 * shared, so the notice and the formula cannot drift apart. Neither takes a
 * slot: both rules follow the material into whatever it is used for, so the
 * same line is correct under a cabinet carcass, a cut-list piece, a countertop,
 * a drawer front or a panel.
 */
export function specialMaterialFlags(
  material: SpecialMaterialInput,
  glassSandingMarkup: string | undefined,
  t: TFunction,
): CatalogItemFlag[] {
  return specialMaterialRules(material).map((rule): CatalogItemFlag => {
    if (rule === 'GLASS_SANDING') {
      return {
        badge: t('pricing.specialMaterial.glassSanding.badge'),
        tone: 'warning',
        message: t('pricing.specialMaterial.glassSanding.message', {
          markup: formatMarkup(glassSandingMarkup, t),
        }),
      };
    }
    return {
      badge: t('pricing.specialMaterial.fixedPrice.badge'),
      tone: 'info',
      message: t('pricing.specialMaterial.fixedPrice.message'),
    };
  });
}

/**
 * "×1.30 (+30%)" when the constants have loaded, a wording that names the
 * setting instead when they have not.
 *
 * The picker must stay useful while `GET /pricing/constants` is in flight or has
 * failed, and a half-rendered "×" with nothing after it would read as a bug.
 */
export function formatMarkup(markup: string | undefined, t: TFunction): string {
  const value = markup === undefined ? NaN : Number.parseFloat(markup);
  if (!Number.isFinite(value) || value <= 0) {
    return t('pricing.specialMaterial.glassSanding.markupUnknown');
  }
  const percent = Math.round((value - 1) * 100);
  return t('pricing.specialMaterial.glassSanding.markupValue', {
    markup: value.toFixed(2),
    percent,
  });
}
